import React from 'react';
import Head from 'next/head';
import NextLink from 'next/link';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Link,
  UnorderedList,
  ListItem,
  Divider,
  useColorModeValue,
} from '@chakra-ui/react';
import { getPageTitle, getMetaDescription } from '../src/lib/config';

export default function PrivacyPolicyPage() {
  const bgColor = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const headingColor = useColorModeValue('gray.900', 'white');

  return (
    <>
      <Head>
        <title>{getPageTitle('プライバシーポリシー')}</title>
        <meta name="description" content={getMetaDescription('プライバシーポリシー')} />
      </Head>

      <Box bg={bgColor} minH="100vh" py={12}>
        <Container maxW="3xl" px={4}>
          <VStack spacing={8} align="stretch">
            <Box textAlign="center">
              <Link as={NextLink} href="/" fontWeight="semibold" fontSize="lg" _hover={{ textDecoration: 'none' }}>
                LogMe
              </Link>
            </Box>

            <Heading as="h1" size="xl" color={headingColor} textAlign="center">
              プライバシーポリシー
            </Heading>

            <Text color={textColor} fontSize="sm" textAlign="right">
              制定日：2026年3月29日
            </Text>

            <Divider />

            <VStack spacing={6} align="stretch" color={textColor} lineHeight="tall">
              <Text>
                LogMe（以下「当サービス」）は、お客様の個人情報の保護を重要な責務と認識し、以下のとおりプライバシーポリシーを定めます。
              </Text>

              <Box>
                <Heading as="h2" size="md" color={headingColor} mb={3}>
                  1. 収集する情報
                </Heading>
                <Text mb={2}>当サービスは、以下の情報を収集することがあります。</Text>
                <UnorderedList spacing={1} pl={4}>
                  <ListItem>メールアドレス</ListItem>
                  <ListItem>パスワード（暗号化して保存）</ListItem>
                  <ListItem>店舗名・店舗情報</ListItem>
                  <ListItem>従業員情報（氏名、連絡先等）</ListItem>
                  <ListItem>売上・会計データ</ListItem>
                  <ListItem>アクセスログ・操作ログ</ListItem>
                  <ListItem>Cookie・端末情報</ListItem>
                </UnorderedList>
              </Box>

              <Box>
                <Heading as="h2" size="md" color={headingColor} mb={3}>
                  2. 利用目的
                </Heading>
                <Text mb={2}>収集した情報は、以下の目的で利用します。</Text>
                <UnorderedList spacing={1} pl={4}>
                  <ListItem>サービスの提供・運営・改善</ListItem>
                  <ListItem>アカウント管理・本人確認</ListItem>
                  <ListItem>お問い合わせへの対応</ListItem>
                  <ListItem>サービスに関するご案内・通知の送信</ListItem>
                  <ListItem>不正利用の防止・セキュリティ確保</ListItem>
                  <ListItem>利用状況の分析・統計処理（個人を特定しない形式）</ListItem>
                </UnorderedList>
              </Box>

              <Box>
                <Heading as="h2" size="md" color={headingColor} mb={3}>
                  3. 第三者提供
                </Heading>
                <Text>
                  当サービスは、法令に基づく場合を除き、お客様の同意なく個人情報を第三者に提供することはありません。
                  ただし、以下の場合を除きます。
                </Text>
                <UnorderedList spacing={1} pl={4} mt={2}>
                  <ListItem>法令に基づく開示要請があった場合</ListItem>
                  <ListItem>人の生命・身体・財産の保護に必要な場合</ListItem>
                  <ListItem>サービス提供に必要な業務委託先への提供（適切な管理のもと）</ListItem>
                </UnorderedList>
              </Box>

              <Box>
                <Heading as="h2" size="md" color={headingColor} mb={3}>
                  4. 安全管理措置
                </Heading>
                <Text>
                  当サービスは、個人情報の漏洩・滅失・毀損を防止するため、適切な安全管理措置を講じます。
                  データの暗号化、アクセス制御、監査ログの記録等の技術的・組織的措置を実施しています。
                </Text>
              </Box>

              <Box>
                <Heading as="h2" size="md" color={headingColor} mb={3}>
                  5. Cookieの使用
                </Heading>
                <Text>
                  当サービスは、利便性向上およびサービス改善のためにCookieを使用します。
                  ブラウザの設定によりCookieの受け入れを拒否することも可能ですが、
                  一部機能が利用できなくなる場合があります。
                </Text>
              </Box>

              <Box>
                <Heading as="h2" size="md" color={headingColor} mb={3}>
                  6. 個人情報の開示・訂正・削除
                </Heading>
                <Text>
                  お客様は、ご自身の個人情報について、開示・訂正・削除を求めることができます。
                  ご希望の場合は、下記のお問い合わせ先までご連絡ください。
                </Text>
              </Box>

              <Box>
                <Heading as="h2" size="md" color={headingColor} mb={3}>
                  7. ポリシーの変更
                </Heading>
                <Text>
                  当サービスは、必要に応じて本ポリシーを変更することがあります。
                  重要な変更がある場合は、サービス内またはメールにてお知らせします。
                </Text>
              </Box>

              <Box>
                <Heading as="h2" size="md" color={headingColor} mb={3}>
                  8. お問い合わせ
                </Heading>
                <Text>
                  個人情報の取扱いに関するお問い合わせは、support@logmeapp.com までご連絡ください。
                </Text>
              </Box>
            </VStack>

            <Divider />

            <Text textAlign="center" fontSize="sm" color="gray.500">
              <Link as={NextLink} href="/" _hover={{ textDecoration: 'underline' }}>
                LogMe トップページへ戻る
              </Link>
            </Text>
          </VStack>
        </Container>
      </Box>
    </>
  );
}
